import React, { createContext, useContext, ReactNode } from 'react';
import { useAuthSimple } from '../hooks/useAuth';

interface EmpresaContextType {
  empresaId: string | null;
  userId: string | null;
  userType: 'master' | 'grupo' | 'empresa' | null;
  isUserFromEmpresa: boolean;
  isUserFromGrupo: boolean;
  isMaster: boolean;
}

const EmpresaContext = createContext<EmpresaContextType | null>(null);

export const useEmpresaContext = (): EmpresaContextType => {
  const context = useContext(EmpresaContext);
  if (!context) {
    throw new Error('useEmpresaContext deve ser usado dentro de um EmpresaProvider');
  }
  return context;
};

interface EmpresaProviderProps {
  children: ReactNode;
}

export const EmpresaProvider: React.FC<EmpresaProviderProps> = ({ children }) => {
  const { user } = useAuthSimple();

  const empresaId = user?.empresaId || null;
  const userId = user?.id || null;
  const userType = user?.type || null;
  const isUserFromEmpresa = user?.type === 'empresa';
  const isUserFromGrupo = user?.type === 'grupo';
  const isMaster = user?.type === 'master';

  const value: EmpresaContextType = {
    empresaId,
    userId,
    userType,
    isUserFromEmpresa,
    isUserFromGrupo,
    isMaster
  };

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  );
};

// Hook para adicionar automaticamente empresaId aos dados de requisição
export const useAutoEmpresaId = () => {
  const { empresaId, isUserFromEmpresa } = useEmpresaContext();

  const addEmpresaId = <T extends Record<string, any>>(data: T): T & { empresaId?: string } => {
    // Só adiciona empresaId automaticamente se o usuário for de empresa e não for master
    if (isUserFromEmpresa && empresaId) {
      return { ...data, empresaId };
    }
    return data;
  };

  return { addEmpresaId, empresaId, hasEmpresa: !!empresaId };
};